import { Button as PrimeButton } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import React, { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import Swal from "sweetalert2";
import { Tier } from "../../models/tier.model";
import { tierService } from "../../service/tier.Service";
import PageHeader from "../PageHeader";

const initialTier: Partial<Tier> = {
  name: "",
  description: "",
};

const TierComponent: React.FC = () => {
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [allTiers, setAllTiers] = useState<Tier[]>([]);
  const [selectedTierFilter, setSelectedTierFilter] = useState<Tier | null>(
    null
  );
  const [listLoading, setListLoading] = useState(true);
  const [tier, setTier] = useState<Partial<Tier>>(initialTier);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [page, setPage] = useState(() => {
    const savedPage = localStorage.getItem("tierTablePage");
    return savedPage ? parseInt(savedPage) : 1;
  });
  const [rows, setRows] = useState(() => {
    const savedRows = localStorage.getItem("tierTableRows");
    return savedRows ? parseInt(savedRows) : 10;
  });
  const [totalRecords, setTotalRecords] = useState(0);
  const [globalFilter, setGlobalFilter] = useState("");
  const [sortField, setSortField] = useState(() => {
    const savedSort = localStorage.getItem("tierTableSortField");
    return savedSort || "name";
  });
  const [sortOrder, setSortOrder] = useState<1 | -1>(() => {
    const savedOrder = localStorage.getItem("tierTableSortOrder");
    return (savedOrder ? parseInt(savedOrder) : 1) as 1 | -1;
  });

  const loadAllTiers = async () => {
    try {
      const result = await tierService.getAllPaginated({
        pageNumber: 1,
        pageSize: 1000, // Large number to get all tiers
        sortField: "name",
        sortOrder: "asc",
      });
      setAllTiers(
        (result?.items ?? []).map((item: any) => ({
          ...item,
          id: typeof item.id === "string" ? parseInt(item.id, 10) : item.id,
        }))
      );
    } catch (err) {
      console.error("Error fetching all tiers", err);
    }
  };

  useEffect(() => {
    const fetchTiers = async () => {
      setListLoading(true);
      try {
        const filter = selectedTierFilter
          ? selectedTierFilter.name
          : globalFilter;
        const result = await tierService.getAllPaginated({
          pageNumber: page,
          pageSize: rows,
          sortField,
          sortOrder: sortOrder === -1 ? "desc" : "asc",
          globalFilter: filter,
        });
        setTiers(
          (result?.items ?? []).map((item: any) => ({
            ...item,
            id: typeof item.id === "string" ? parseInt(item.id, 10) : item.id,
          }))
        );
        setTotalRecords(result?.totalCount ?? 0);
      } catch {
        setTiers([]);
        setTotalRecords(0);
      } finally {
        setListLoading(false);
      }
    };
    fetchTiers();
  }, [
    success,
    page,
    rows,
    sortField,
    sortOrder,
    globalFilter,
    selectedTierFilter,
    refreshKey,
  ]);
  const handleRefresh = () => setRefreshKey((k) => k + 1);

  useEffect(() => {
    loadAllTiers(); // Load all tiers for the dropdown
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setTier({ ...tier, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      if (tier.id) {
        const updated = await tierService.update(tier.id, tier as Tier);
        setTiers((prev) => prev.map((t) => (t.id === tier.id ? updated : t)));
        Swal.fire({
          toast: true,
          position: "bottom",
          icon: "success",
          title: "Tier updated successfully.",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          customClass: { popup: "swal2-toast-bottom" },
        });
      } else {
        const created = await tierService.create(tier as Tier);
        setTiers((prev) => [...prev, created]);
        Swal.fire({
          toast: true,
          position: "bottom",
          icon: "success",
          title: "Tier created successfully.",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          customClass: { popup: "swal2-toast-bottom" },
        });
      }
      setTier(initialTier);
      setShowCreateForm(false);
      setSuccess(true);
    } catch (err: any) {
      setError(
        err?.response?.data?.message || err.message || "Error saving tier"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (row: Tier) => {
    setTier(row);
    setShowCreateForm(true);
  };

  const handleDelete = async (row: Tier) => {
    if (!row.id) return;
    try {
      await tierService.delete(row.id);
      // Update the local state immediately
      setTiers((prev) => prev.filter((t) => t.id !== row.id));
      setSuccess(true);
      Swal.fire({
        toast: true,
        position: "bottom",
        icon: "success",
        title: "Tier deleted successfully.",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        customClass: { popup: "swal2-toast-bottom" },
      });
    } catch {
      setError("Failed to delete tier");
      Swal.fire({
        toast: true,
        position: "bottom",
        icon: "error",
        title: "Failed to delete tier.",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        customClass: { popup: "swal2-toast-bottom" },
      });
    }
  };

  const actionBodyTemplate = (rowData: Tier) => (
    <div className="d-flex align-items-center gap-2">
      <Button
        variant="link"
        className="p-0"
        onClick={() => handleEdit(rowData)}
        title="Edit"
        style={{ color: "#198754" }}
      >
        <Icon.PencilSquare size={20} />
      </Button>
      <Button
        variant="link"
        className="p-0"
        onClick={() => handleDelete(rowData)}
        title="Delete"
        style={{ color: "#dc3545" }}
      >
        <Icon.Trash size={20} />
      </Button>
    </div>
  );

  return (
    <div className="content">
      <PageHeader
        title="Tier List"
        beforeTitle={
          <PrimeButton
            icon="pi pi-refresh"
            rounded
            text
            severity="secondary"
            onClick={handleRefresh}
            tooltip="Refresh"
            tooltipOptions={{ position: "bottom" }}
            aria-label="Refresh Tiers"
          />
        }
      />
      <div className="content-body">
        <div className="container mt-4">
          <div className="row shadow">
            <div className="col-md-12 mb-3">
              <div className="d-flex align-items-center justify-content-between gap-3 my-3">
                <IconField iconPosition="left" style={{ width: "200px" }}>
                  <InputIcon>
                    <Icon.Search className="tx-12" />
                  </InputIcon>
                  <InputText
                    value={globalFilter}
                    onChange={(e) => {
                      setGlobalFilter(e.target.value);
                      setSelectedTierFilter(null);
                      setPage(1);
                      localStorage.setItem("tierTablePage", "1");
                    }}
                    placeholder="Search"
                    style={{ height: "38px" }}
                  />
                </IconField>
                <div className="d-flex align-items-center gap-3">
                  <Dropdown
                    value={selectedTierFilter}
                    onChange={(e: { value: Tier | null }) => {
                      setSelectedTierFilter(e.value);
                      setPage(1);
                      setGlobalFilter("");
                    }}
                    options={allTiers}
                    optionLabel="name"
                    placeholder="Select Tier"
                    style={{ width: "200px", height: "38px" }}
                    appendTo="self"
                    showClear
                    filter
                    filterBy="name,description"
                  />
                  <Button
                    className="btn btn-primary"
                    style={{ height: "38px", width: "200px" }}
                    onClick={() => {
                      setTier(initialTier);
                      setShowCreateForm(true);
                    }}
                  >
                    <Icon.PlusCircle className="me-1" /> Create Tier
                  </Button>
                </div>
              </div>

              <DataTable
                value={tiers}
                dataKey="id"
                paginator
                first={(page - 1) * rows}
                rows={rows}
                totalRecords={totalRecords}
                onPage={(e) => {
                  const newPage = (e.page ?? 0) + 1;
                  const newRows = e.rows;
                  setPage(newPage);
                  setRows(newRows);
                  localStorage.setItem("tierTablePage", newPage.toString());
                  localStorage.setItem("tierTableRows", newRows.toString());
                }}
                onSort={(e) => {
                  const newSortField = e.sortField || "name";
                  const newSortOrder = e.sortOrder as 1 | -1;
                  setSortField(newSortField);
                  setSortOrder(newSortOrder);
                  localStorage.setItem("tierTableSortField", newSortField);
                  localStorage.setItem(
                    "tierTableSortOrder",
                    newSortOrder.toString()
                  );
                }}
                sortField={sortField}
                sortOrder={sortOrder}
                lazy
                loading={listLoading}
                rowsPerPageOptions={[5, 10, 20]}
                stripedRows
                sortMode="single"
                emptyMessage="No tiers found."
                className="grid-table"
              >
                <Column field="name" header="Name" sortable />
                <Column field="description" header="Description" sortable />
                <Column
                  header="Actions"
                  body={actionBodyTemplate}
                  style={{ minWidth: "120px" }}
                />
              </DataTable>

              {showCreateForm && (
                <div
                  className="modal show d-block"
                  tabIndex={-1}
                  style={{ background: "rgba(0,0,0,0.3)" }}
                >
                  <div className="modal-dialog">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">
                          {tier.id ? "Update Tier" : "Create Tier"}
                        </h5>
                        <button
                          type="button"
                          className="btn-close"
                          onClick={() => setShowCreateForm(false)}
                        ></button>
                      </div>
                      <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                          <div className="mb-3">
                            <label className="form-label">Name:</label>
                            <input
                              className="form-control"
                              name="name"
                              value={tier.name || ""}
                              onChange={handleChange}
                              required
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label">Description:</label>
                            <textarea
                              className="form-control"
                              name="description"
                              value={tier.description || ""}
                              onChange={handleChange}
                            />
                          </div>
                          {error && (
                            <div className="alert alert-danger">{error}</div>
                          )}
                        </div>
                        <div className="modal-footer">
                          <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading}
                          >
                            {loading
                              ? tier.id
                                ? "Updating..."
                                : "Creating..."
                              : tier.id
                              ? "Update Tier"
                              : "Create Tier"}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setShowCreateForm(false)}
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TierComponent;
export { TierComponent };
